import React, { useContext, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import AnDateField from '../../../components/DateTime/DateField'
import GapSeverityChip from '../components/GapSeverityChip'

const SOURCE_LABEL = { policy: 'policy', sop: 'SOP' }

// Records that a policy/SOP has been reviewed and sets its next review
// date, without going through the full "upload a new version" flow. The
// reviewer must tick off each required action and describe what was
// actually done - reviewing blind (without seeing what the gap asked for)
// used to be possible and defeated the point. Works for both CQC Centre's
// gap-driven entry point (this file) and PolicyPage.jsx, which passes a
// gap-shaped object built from the policy it's viewing.
const MarkGapReviewedDialog = ({ gap, onClose, onSaved }) => {
  const { setToast } = useContext(GlobalContext)
  const [reviewDate, setReviewDate] = useState(moment().add(12, 'months'))
  const [actionTaken, setActionTaken] = useState('')
  const [saving, setSaving] = useState(false)

  const sourceLabel = SOURCE_LABEL[gap.sourceType] || 'record'

  // The actions the gap is asking for - kept as a list so gaps can carry
  // multiple requirements in future
  const requiredActions = Array.isArray(gap.requiredActions)
    ? gap.requiredActions
    : [gap.message].filter(Boolean)
  const [checkedActions, setCheckedActions] = useState(() =>
    requiredActions.map(() => false),
  )

  const allChecked = checkedActions.every(Boolean)
  const canSubmit = allChecked && !!actionTaken.trim() && !!reviewDate

  const toggleAction = (index) => {
    setCheckedActions((prev) =>
      prev.map((checked, i) => (i === index ? !checked : checked)),
    )
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!canSubmit) return
    setSaving(true)
    API.put(`/${gap.sourceType}/${gap.relatedRecordId}/review`, {
      reviewDate: reviewDate.toISOString(),
      actionTaken: actionTaken.trim(),
    })
      .then((res) => {
        if (res.status === 200) {
          setToast?.({
            message: `Review recorded - next review ${reviewDate.format('Do MMM YYYY')}`,
          })
          onSaved?.()
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setSaving(false))
  }

  return (
    <Stack
      gap={2}
      component="form"
      onSubmit={handleSubmit}
      sx={{ maxWidth: 520 }}
    >
      <Typography variant="title2">Review Actions</Typography>

      <Stack gap={1}>
        <Typography variant="subtitle2" fontWeight={700}>
          Action{requiredActions.length === 1 ? '' : 's'} required
        </Typography>
        {requiredActions.map((action, index) => (
          <Stack
            key={index}
            gap={1}
            alignItems="flex-start"
            sx={{
              p: 1,
              border: '1px solid',
              borderColor: checkedActions[index] ? 'success.main' : 'divider',
              borderRadius: 1,
            }}
          >
            <Stack
              direction="row"
              gap={1}
              justifyContent="space-between"
              width="100%"
            >
              {gap.severity && <GapSeverityChip severity={gap.severity} />}
              <Stack direction="row" gap={1} alignItems="center">
                <Typography variant="body2" fontWeight={800}>
                  Mark as Reviewed
                </Typography>
                <Switch
                  size="small"
                  checked={checkedActions[index]}
                  onChange={() => toggleAction(index)}
                  color='success'
                />
              </Stack>
            </Stack>
            <Typography variant="body2">{action}</Typography>
          </Stack>
        ))}
      </Stack>

      <TextField
        label="Action taken"
        value={actionTaken}
        onChange={(e) => setActionTaken(e.target.value)}
        multiline
        minRows={2}
        required
        placeholder={`Describe what was done to address this - e.g. "Reviewed the ${sourceLabel} against current guidance; no changes required."`}
      />

      <Alert severity="info" variant="outlined">
        This records that the {sourceLabel} has been reviewed and sets its next
        review date - it doesn&apos;t require uploading a new version.
      </Alert>

      <AnDateField
        label="Next review date"
        date={reviewDate}
        onChange={(m) => setReviewDate(m)}
      />

      <Stack direction="row" gap={1} justifyContent="flex-end">
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="submit"
          variant="contained"
          color="success"
          disabled={saving || !canSubmit}
        >
          {saving ? 'Saving…' : 'Record Review'}
        </Button>
      </Stack>
    </Stack>
  )
}

export default MarkGapReviewedDialog
